import * as React from 'react';
import { PickInfo } from '@deck.gl/core/lib/deck';
import { GridCellLayer, TextLayer } from '@deck.gl/layers';
import classnames from 'classnames';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { createArtikelDetailUrl, formatDecimal, formatMoney } from 'utils/formatter';
import { useAuftragPositionList } from 'api/auftragPosition';
import { OpenLinkIcon } from 'formComponents/OpenLinkIcon/OpenLinkIcon';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector } from 'store/lookups/selector';
import { useAuftragList } from 'api/auftrag';
import { AxisLabel, Pillar } from './types';

export const styles = mergeStyleSets({
    popover: {
        backgroundColor: '#BBB',
        borderRadius: 6,
        border: '1px solid #AAA',
        boxShadow: '0 3px 4px 0 #CCC',
    },
    tooltipContainer: {
        position: 'absolute',
        backgroundColor: '#F3F3F3',
        borderRadius: 6,
        // border: '1px solid #AAA',
        boxShadow: '0 3px 4px 0 #CCC',
        opacity: 1,
    },
    tooltipTransparent: {
        opacity: 0.9,
    },
    tooltipContent: {
        padding: 10,
        // width: '100%',
        minWidth: 270,
        maxHeight: 320,
        display: 'grid',
        gridTemplateRows: 'max-content max-content 1fr',
    },
    tooltipKey: {
        display: 'inline-block',
        position: 'relative',
        width: '30%',
        fontSize: 14,
        verticalAlign: 'top',
    },
    tooltipValue: {
        position: 'relative',
        display: 'inline-block',
        fontSize: 14,
        width: '70%',
        overflow: 'hidden',
        fontWeight: 'bold',
        textAlign: 'right',
        verticalAlign: 'top',
        maxWidth: 300,
        wordBreak: 'break-word',
    },
    tooltipTitle: {
        display: 'inline-block',
        position: 'relative',
        width: '100%',
        fontSize: 14,
        verticalAlign: 'top',
        fontWeight: 'bold',
        textAlign: 'left',
        marginRight: 30,
    },
    tooltipText: {
        display: 'inline-block',
        position: 'relative',
        width: '100%',
        fontSize: 14,
        verticalAlign: 'top',
        textAlign: 'left',
    },
    tooltipLine: {
        display: 'inline-block',
        position: 'relative',
        borderTop: '1px solid #CCC',
        width: '100%',
        height: 6,
        marginTop: 3,
    },
    positions: {
        overflowY: 'scroll',
    },
    tableHeadRow: {
        fontWeight: 700,
    },
    tableBodyRow: {
        fontWeight: 300,
    },
    tableCellRight: {
        textAlign: 'right',
    },
});

interface ContextMenuProps {
    parentRef: React.RefObject<HTMLDivElement>;
    pickInfo: PickInfo<GridCellLayer<Pillar>> | PickInfo<TextLayer<AxisLabel>> | null;
}

interface Position {
    top: number;
    left: number;
}

interface Menu {
    pinned: boolean;
    position: Position;
    pickInfo: PickInfo<GridCellLayer<Pillar>> | PickInfo<TextLayer<AxisLabel>> | null;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ parentRef, pickInfo }) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [menu, setMenu] = React.useState<Menu | null>(null);
    const [mouseDownPosition, setMouseDownPosition] = React.useState<Position | null>();
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);

    const pillar = React.useMemo(
        () =>
            (menu?.pickInfo?.layer instanceof GridCellLayer && ((menu?.pickInfo?.object as unknown) as Pillar)) || null,
        [menu?.pickInfo?.layer, menu?.pickInfo?.object],
    );

    const auftragList = useAuftragList(
        {
            auNrList: pillar?.auNrList || [],
            withKunde: true,
        },
        {
            enabled: !!pillar?.artikelNr,
        },
    );

    const auftragPositionList = useAuftragPositionList(
        {
            auNrList: pillar?.auNrList || [],
        },
        {
            enabled: !!pillar?.artikelNr,
        },
    );

    const mouseDownListener = React.useCallback(
        (event: MouseEvent) => {
            if (!menu?.pinned) {
                setMouseDownPosition({
                    // top: event.clientY,
                    top: event.y,
                    // left: event.clientX,
                    left: event.x,
                });
            }
        },
        [menu],
    );

    const mouseUpListener = React.useCallback(
        (event: MouseEvent) => {
            if (mouseDownPosition && event.y === mouseDownPosition.top && event.x === mouseDownPosition.left) {
                const parentRect = parentRef?.current?.getBoundingClientRect();
                if (!parentRect) {
                    return;
                }

                setMenu({
                    pinned: true,
                    position: { top: event.clientY - parentRect.top, left: event.clientX - parentRect.left },
                    // position: { top: event.y, left: event.x },
                    pickInfo: pickInfo || null,
                });
            }

            if (mouseDownPosition) {
                setMouseDownPosition(null);
            }
        },
        [pickInfo, mouseDownPosition, parentRef],
    );

    const mouseMoveListener = React.useCallback(
        (event: MouseEvent) => {
            const parentRect = parentRef?.current?.getBoundingClientRect();
            if (!parentRect) {
                return;
            }

            if (menu?.pinned) {
                if (!containerRef?.current?.contains(event.target as HTMLElement)) {
                    // mouse moved outside context menu -> unpin it
                    setMenu({
                        pinned: false,
                        position: { top: event.clientY - parentRect.top, left: event.clientX - parentRect.left },
                        // position: { top: event.y, left: event.x },
                        pickInfo: pickInfo || null,
                    });
                }
            } else {
                setMenu({
                    pinned: false,
                    position: { top: event.clientY - parentRect.top, left: event.clientX - parentRect.left },
                    // position: { top: event.y, left: event.x },
                    pickInfo: pickInfo || null,
                });
            }
        },
        [menu, pickInfo, parentRef],
    );

    React.useEffect(() => {
        const parent = parentRef.current;
        if (!parent) {
            return undefined;
        }

        parent.addEventListener('mousedown', mouseDownListener);
        parent.addEventListener('mouseup', mouseUpListener);
        parent.addEventListener('mousemove', mouseMoveListener);

        return (): void => {
            parent.removeEventListener('mousedown', mouseDownListener);
            parent.removeEventListener('mouseup', mouseUpListener);
            parent.removeEventListener('mousemove', mouseMoveListener);
        };
    }, [parentRef, mouseDownListener, mouseUpListener, mouseMoveListener]);

    if (!menu || !menu.pickInfo || mouseDownPosition) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className={classnames(styles.popover, styles.tooltipContainer, {
                [styles.tooltipTransparent]: !menu.pinned,
            })}
            style={{
                top: menu.position.top + (menu.pinned ? -1 : 1),
                left: menu.position.left + (menu.pinned ? -1 : 1),
            }}
        >
            {pillar?.artikelNr && (
                <div className={styles.tooltipContent}>
                    <div className={styles.tooltipTitle}>
                        {artikelGruppenByNr[pillar.artikelGruppeNr]?.text || pillar.artikelGruppeNr}
                        {' - '}
                        {pillar.artikelNr}
                        <OpenLinkIcon link={createArtikelDetailUrl(pillar.artikelGruppeNr, pillar.artikelNr)} />
                    </div>
                    <div className={styles.tooltipText}>
                        Monat : {pillar.month}, Menge: {formatDecimal(pillar.totalMenge)}
                    </div>
                    <div className={styles.positions}>
                        <table>
                            <thead>
                                <tr className={styles.tableHeadRow}>
                                    <td>Auftrag-Nr.</td>
                                    <td>Kunde</td>
                                    <td className={styles.tableCellRight}>Menge</td>
                                    <td className={styles.tableCellRight}>Netto</td>
                                </tr>
                            </thead>
                            <tbody>
                                {(auftragPositionList.data || [])
                                    .filter(
                                        (position) =>
                                            position.artikelGruppeNr === pillar?.artikelGruppeNr &&
                                            position.artikelNr === pillar?.artikelNr,
                                    )
                                    .sort((a, b) =>
                                        (a.auNr + a.pos.toString().padStart(4, '0')).localeCompare(
                                            b.auNr + b.pos.toString().padStart(4, '0'),
                                        ),
                                    )
                                    .map((position) => {
                                        const auftrag = (auftragList.data || []).find((a) => a.nr === position.auNr);

                                        return (
                                            <tr className={styles.tableBodyRow} key={position.auNr}>
                                                <td>{position.auNr}</td>
                                                <td>{auftrag?.kunde?.name1 || '?'}</td>
                                                <td className={styles.tableCellRight}>{position.posMenge}</td>
                                                <td className={styles.tableCellRight}>
                                                    {formatMoney(position.posNetto)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};
